import { send } from "@/lib/deriv";
import { api } from "@/lib/api";

export const SYMBOL = "1HZ100V";
export const lastDigit = (v) => parseInt(String(Number(v).toFixed(2)).slice(-1), 10);
/* 📏 pip-aware batch digit extraction — Deriv settles digits on the
   pip-FORMATTED quote (655.30 → 0), but JS numbers drop trailing zeros.
   Infer the max decimals across the batch, then format before slicing. */
export const lastDigitsOf = (prices) => {
  let dec = 0;
  prices.forEach((p) => {
    const s = String(p);
    const i = s.indexOf(".");
    const d = i < 0 ? 0 : s.length - i - 1;
    if (d > dec) dec = d;
  });
  return prices.map((p) => parseInt(Number(p).toFixed(dec).slice(-1), 10));
};
const digitFromPoc = (poc) => {
  const dv = poc?.exit_tick_display_value ?? poc?.exit_spot_display_value;
  if (dv != null) {
    const m = String(dv).match(/(\d)(?!.*\d)/);
    if (m) return parseInt(m[1], 10);
  }
  const ev = poc?.exit_spot ?? poc?.exit_tick;
  if (ev == null) return null;
  // 📏 infer the market's pip decimals from the ENTRY display value — rounding
  // a 3-decimal quote to 2 decimals shows the WRONG settle digit
  const ref = poc?.entry_tick_display_value ?? poc?.entry_spot_display_value;
  const dot = ref != null ? String(ref).indexOf(".") : -1;
  if (dot >= 0) {
    const dec = String(ref).length - dot - 1;
    return parseInt(Number(ev).toFixed(dec).slice(-1), 10);
  }
  return lastDigit(ev);
};

/** Play one real Deriv digit/rise-fall contract. barrier is required for
 * DIGITMATCH / DIGITOVER / DIGITUNDER. Returns payout so games can show the
 * real multiple (e.g. Lucky Number ~×9). */
export async function playContract({ contractType, barrier = null, stake, currency = "USD", accountId, source, strategy, symbol = SYMBOL, duration = 1 }) {
  const params = { amount: stake, basis: "stake", currency, contract_type: contractType, duration, duration_unit: "t", underlying_symbol: symbol };
  if (barrier != null) params.barrier = String(barrier);
  // 🚀 Buy DIRECTLY with parameters — one round-trip and ZERO `proposal` calls,
  // so bursts never touch Deriv's proposal rate limit (same as Pro Trade).
  let buy = await send({ buy: 1, price: stake, parameters: params });
  // Deriv throttles buys per minute too — escalating backoff instead of failing
  for (const wait of [2000, 5000, 10000]) {
    if (!(buy?.error && /rate limit/i.test(buy.error.message || ""))) break;
    await new Promise((r) => setTimeout(r, wait));
    buy = await send({ buy: 1, price: stake, parameters: params });
  }
  let estPayout = buy?.buy?.payout || 0;
  if (buy?.error && !/rate limit/i.test(buy.error.message || "")) {
    // 🧯 fallback: classic subscribe-proposal → buy path (kept for any
    // market/account where the parameters buy is rejected)
    const req = { proposal: 1, subscribe: 1, ...params };
    const prop = await send(req);
    if (prop?.error) throw new Error(prop.error.message || "Deriv rejected the proposal");
    if (!prop?.proposal?.id) throw new Error("No proposal returned — try again");
    buy = await send({ buy: prop.proposal.id, price: prop.proposal.ask_price ?? stake * 1.02 });
    const sid = prop?.subscription?.id;
    if (sid) send({ forget: sid }).catch(() => {});
    estPayout = prop.proposal.payout || 0;
  }
  if (buy?.error) throw new Error(buy.error.message || "Buy rejected");
  const cid = buy?.buy?.contract_id;
  if (!cid) throw new Error("Buy did not return a contract — try again");
  api.post("/track/trade", {
    account_id: accountId, contract_id: String(cid), symbol,
    contract_type: contractType, stake, payout: estPayout || stake,
    currency, strategy, source,
  }).catch(() => {});
  let poc = null;
  for (let i = 0; i < 20; i += 1) {
    await new Promise((r) => setTimeout(r, 1000));
    try {
      const res = await send({ proposal_open_contract: 1, contract_id: cid });
      poc = res?.proposal_open_contract;
      if (poc?.is_sold) break;
    } catch { /* retry */ }
  }
  const profit = Math.round((poc?.profit != null ? Number(poc.profit) : -stake) * 100) / 100;
  const digit = digitFromPoc(poc);
  const won = profit > 0;
  api.patch(`/track/trade/${cid}`, { status: won ? "won" : "lost", profit }).catch(() => {});
  return { won, digit, profit, stake, payout: estPayout };
}

/** Play one real Even/Odd contract (best-priced digit contract, ~×1.95). */
export async function playEvenOdd({ side, stake, currency = "USD", accountId, source, strategy, symbol = SYMBOL }) {
  const ct = side === "EVEN" ? "DIGITEVEN" : "DIGITODD";
  const res = await playContract({ contractType: ct, stake, currency, accountId, source, strategy, symbol });
  return { ...res, side };
}
